import { useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { ReactComponent as RightArrowIcon } from '../../../../../../assets/icons/arrow-right.svg';
import { ReactComponent as PlusIcon } from '../../../../../../assets/icons/plus.svg';
import CustomButton from '../../../../../../shared/components/Buttons/CustomButton/CustomButton';
import Tooltip from '../../../../../../shared/components/Globals/Tooltip/Tooltip';
import ActivationMissingInfoModal from '../../../../../../shared/components/Modals/ActivationMissingInfoModal/ActivationMissingInfoModal';
import { PROSPECTS_ROUTE } from '../../../../../../shared/constants/auth';
import {
  CLICK_ACTION,
  COMPLETE_ACTIVATION_NOW,
  MOVE_TO_ACTIVATION,
  SAVE_AS_PENDING_ACTIVATION,
  SOURCE_PANEL
} from '../../../../../../shared/constants/tagManager';
import { useProspects } from '../../../../../../shared/hooks/useProspect/useProspect';
import { useQuoteActions } from '../../../../../../shared/hooks/useQuoteActions/useQuoteActions';
import type { IProspect } from '../../../../../../shared/interfaces/prospects';
import type { IQuote } from '../../../../../../shared/interfaces/quotes';
import TagManagerService from '../../../../../../shared/services/gtm/tagManagerService';
import { TRANSLATIONS_PROSPECTS } from '../../../../../../shared/translations/prospects';
import { areAllChecksDone, buildProspectActivationGroup } from '../../../../../../shared/utils/checks/activationChecks';
import ProspectActionsMenu from '../../components/ProspectActionsMenu/ProspectActionsMenu';
import QuotationCard from '../../components/QuotationCard/QuotationCard';
import QuoteFeedback from '../../components/QuoteFeedback/QuoteFeedback';
import './QuotedSection.scss';

interface IQuotedSectionProps {
  prospect: IProspect;
  quotes: IQuote[];
  onRequestActivation: (id: number) => void;
  onMarkAsLost: (id: number) => void;
  onMarkAsInactive: (id: number) => void;
  isUpdatingStatus?: boolean;
}

const QuotedSection = ({
  prospect,
  quotes,
  onRequestActivation,
  onMarkAsLost,
  onMarkAsInactive,
  isUpdatingStatus = false
}: IQuotedSectionProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isActivationModalOpen, setIsActivationModalOpen] = useState(false);
  const { selectedActivationGroup } = useProspects();
  const {
    localQuotes,
    toast,
    setToast,
    unacceptTarget,
    acceptingQuoteId,
    downloadingQuoteId,
    handleNewQuote,
    handleEditQuote,
    handleDownloadQuote,
    handleAcceptQuote,
    handleConfirmUnaccept,
    handleCancelUnaccept
  } = useQuoteActions(quotes);

  const acceptedCount = localQuotes.filter((q) => q.status === 'accepted').length;
  const hasAccepted = acceptedCount > 0;

  const handleMoveToActivation = () => {
    TagManagerService.pushEvent(CLICK_ACTION, MOVE_TO_ACTIVATION, SOURCE_PANEL);
    const prospectGroup = selectedActivationGroup ?? buildProspectActivationGroup(prospect);
    if (areAllChecksDone([prospectGroup])) {
      onRequestActivation(prospect.id);
    } else {
      setIsActivationModalOpen(true);
    }
  };

  const handleSaveAsPending = () => {
    TagManagerService.pushEvent(CLICK_ACTION, SAVE_AS_PENDING_ACTIVATION);
    setIsActivationModalOpen(false);
    onRequestActivation(prospect.id);
  };

  const handleCompleteNow = () => {
    TagManagerService.pushEvent(CLICK_ACTION, COMPLETE_ACTIVATION_NOW);
    setIsActivationModalOpen(false);
    navigate(`${PROSPECTS_ROUTE}/${prospect.id}`);
  };

  return (
    <div className="quoted-section">
      <div className="quoted-section__header">
        <span className="quoted-section__label">{t(TRANSLATIONS_PROSPECTS.detail.quoted.sectionLabel)}</span>
        <CustomButton
          variant="default"
          size="s"
          className="quoted-section__new-quote"
          onClick={() => handleNewQuote(prospect.id)}
        >
          <PlusIcon className="quoted-section__plus" />
          {t(TRANSLATIONS_PROSPECTS.detail.quoted.newQuote)}
        </CustomButton>
      </div>

      <div className="quoted-section__cards">
        {localQuotes.map((q) => (
          <QuotationCard
            key={q.id}
            quote={q}
            onAccept={(qid) => handleAcceptQuote(prospect.id, qid)}
            onEdit={(qid) => handleEditQuote(prospect.id, qid)}
            onDownload={(qid) => handleDownloadQuote(prospect.id, qid)}
            isAccepting={acceptingQuoteId === q.id}
            isDownloading={downloadingQuoteId === q.id}
          />
        ))}
      </div>

      <div className="quoted-section__footer">
        <div className="quoted-section__footer-actions">
          <div className="quoted-section__cta">
            <Tooltip content={t(TRANSLATIONS_PROSPECTS.detail.quoted.acceptTooltip)} disabled={hasAccepted}>
              <CustomButton
                variant={hasAccepted ? 'primary' : 'default'}
                className={`quoted-section__move-btn${hasAccepted ? '' : ' quoted-section__primary--inactive'}`}
                disabled={!hasAccepted}
                onClick={() => hasAccepted && handleMoveToActivation()}
              >
                {t(TRANSLATIONS_PROSPECTS.detail.quoted.moveToActivation)}
                <RightArrowIcon className="quoted-section__arrow" />
              </CustomButton>
            </Tooltip>
          </div>
          <ProspectActionsMenu
            prospectId={prospect.id}
            onMarkAsLost={onMarkAsLost}
            onMarkAsInactive={onMarkAsInactive}
            disabled={isUpdatingStatus}
          />
        </div>
      </div>

      <QuoteFeedback
        prospect={prospect}
        toast={toast}
        setToast={setToast}
        unacceptTarget={unacceptTarget}
        onCancelUnaccept={handleCancelUnaccept}
        onConfirmUnaccept={handleConfirmUnaccept}
      />

      <ActivationMissingInfoModal
        open={isActivationModalOpen}
        prospect={prospect}
        quotes={localQuotes}
        onSaveAsPending={handleSaveAsPending}
        onCompleteNow={handleCompleteNow}
        onClose={() => setIsActivationModalOpen(false)}
      />
    </div>
  );
};

export default QuotedSection;
